import { Text, View, fromTheme, styled } from 'lib_ui-primitives';
import { createElement as rc, useLayoutEffect, useRef, useState } from 'react';
import Chart from './Chart';

const SingleItemContainer = styled(View).attrs({ name: 'TotalsChart' })`
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    gap: ${fromTheme('charts', 'totals', 'gap')};
`;

const ListContainer = styled(View).attrs({ name: 'TotalsListChart' })`
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
    width: 100%;
    gap: ${fromTheme('charts', 'totals', 'list', 'columnGap')};
    padding: ${fromTheme('charts', 'totals', 'list', 'paddingVertical')}
        ${fromTheme('charts', 'totals', 'list', 'paddingHorizontal')};
`;

const ListColumn = styled(View)`
    flex-direction: column;
    flex: 1 1 0;
    min-width: 0;
    gap: ${fromTheme('charts', 'totals', 'list', 'rowGap')};
`;

const ListItemRow = styled(View)`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${fromTheme('charts', 'totals', 'list', 'item', 'paddingVertical')}
        ${fromTheme('charts', 'totals', 'list', 'item', 'paddingHorizontal')};
    border-radius: ${fromTheme('charts', 'totals', 'list', 'item', 'borderRadius')};
    background-color: ${props =>
        props.highlighted ? fromTheme('charts', 'totals', 'list', 'item', 'backgroundColor')(props) : 'transparent'};
`;

const ListItemTitle = styled(Text)`
    font-family: ${fromTheme('charts', 'totals', 'list', 'item', 'title', 'fontFamily')};
    font-size: ${fromTheme('charts', 'totals', 'list', 'item', 'title', 'fontSize')};
    font-weight: ${fromTheme('charts', 'totals', 'list', 'item', 'title', 'fontWeight')};
    line-height: ${fromTheme('charts', 'totals', 'list', 'item', 'title', 'lineHeight')};
    color: ${fromTheme('charts', 'totals', 'list', 'item', 'title', 'color')};
`;

const ListItemCount = styled(Text)`
    font-family: ${fromTheme('charts', 'totals', 'list', 'item', 'count', 'fontFamily')};
    font-size: ${fromTheme('charts', 'totals', 'list', 'item', 'count', 'fontSize')};
    font-weight: ${fromTheme('charts', 'totals', 'list', 'item', 'count', 'fontWeight')};
    line-height: ${fromTheme('charts', 'totals', 'list', 'item', 'count', 'lineHeight')};
    color: ${fromTheme('charts', 'totals', 'list', 'item', 'count', 'color')};
`;

const CountText = styled(Text)`
    font-family: ${fromTheme('charts', 'totals', 'count', 'fontFamily')};
    font-size: ${fromTheme('charts', 'totals', 'count', 'fontSize')};
    font-weight: ${fromTheme('charts', 'totals', 'count', 'fontWeight')};
    line-height: ${fromTheme('charts', 'totals', 'count', 'lineHeight')};
    letter-spacing: ${fromTheme('charts', 'totals', 'count', 'letterSpacing')};
    color: ${fromTheme('charts', 'totals', 'count', 'color')};
`;

const TitleText = styled(Text)`
    font-family: ${fromTheme('charts', 'totals', 'title', 'fontFamily')};
    font-size: ${fromTheme('charts', 'totals', 'title', 'fontSize')};
    font-weight: ${fromTheme('charts', 'totals', 'title', 'fontWeight')};
    line-height: ${fromTheme('charts', 'totals', 'title', 'lineHeight')};
    color: ${fromTheme('charts', 'totals', 'title', 'color')};
`;

function TotalsChart(props) {
    const { data = [] } = props || {};
    const [isWide, setIsWide] = useState(true);
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const observer = new ResizeObserver(entries => {
            const width = entries[0].contentRect.width;
            setIsWide(width > 500);
        });
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Single item - centered large display
    if (data.length === 1) {
        return rc(
            SingleItemContainer,
            { ref: containerRef },
            rc(CountText, null, data[0].count),
            rc(TitleText, null, data[0]._id?.title || '')
        );
    }

    if (!isWide) {
        return rc(
            ListContainer,
            { ref: containerRef },
            rc(
                ListColumn,
                { key: 'single' },
                data.map((result, i) =>
                    rc(
                        ListItemRow,
                        { key: `single-${i}`, highlighted: i % 2 === 0 },
                        rc(ListItemTitle, null, result._id?.title || 'None'),
                        rc(ListItemCount, null, result.count)
                    )
                )
            )
        );
    }

    // Multiple items - 2-column list layout
    const leftColumn = data.filter((_, i) => i % 2 === 0);
    const rightColumn = data.filter((_, i) => i % 2 === 1);

    return rc(
        ListContainer,
        { ref: containerRef },
        rc(
            ListColumn,
            { key: 'left' },
            leftColumn.map((result, i) =>
                rc(
                    ListItemRow,
                    { key: `left-${i}`, highlighted: i % 2 === 0 },
                    rc(ListItemTitle, null, result._id?.title || 'None'),
                    rc(ListItemCount, null, result.count)
                )
            )
        ),
        rc(
            ListColumn,
            { key: 'right' },
            rightColumn.map((result, i) =>
                rc(
                    ListItemRow,
                    { key: `right-${i}`, highlighted: i % 2 === 0 },
                    rc(ListItemTitle, null, result._id?.title || 'None'),
                    rc(ListItemCount, null, result.count)
                )
            )
        )
    );
}

function TotalsElement(props) {
    return Chart(TotalsChart, props);
}

export default TotalsElement;
